import { z } from 'zod/v4';

import { MonetaryValueBackendRuntype } from '../backendTypes';
import { ManufacturerLiteDTORuntype } from '../manufacturer';
import { CustomPartTypeRuntype } from '../part';
import { OfferKind, OneTimeCostDTORuntype, PriceTypeRuntype, StandardPartPriceBreakRuntype } from './offerBackendTypes';
import { PackagingRuntype } from './Packaging';

// The backend's `OfferResponseV2` (`external_api_types::sourcing::offer::v2`) and the `POST /offers/v1/find`
// request and result around it. These shapes differ from the internal offer DTOs in `offerBackendTypes.ts`,
// so the names end in `V2` even where the backend type itself is v1 (quantity, linked location).
// IDs such as the offer ID (`PublicOffTheShelf:<uuid>`) and part IDs (`OffTheShelf:<uuid>`) are opaque strings.

export type QuantityV2 = z.infer<typeof QuantityV2Runtype>;
export const QuantityV2Runtype = z.discriminatedUnion('type', [
    z.object({
        type: z.literal('Number'),
        unit: z.enum(['Pieces']),
        value: z.string(),
    }),
    z.object({
        type: z.literal('Length'),
        unit: z.enum(['Meters', 'Centimeters', 'Millimeters', 'Feet', 'Inches']),
        value: z.string(),
    }),
    z.object({
        type: z.literal('Weight'),
        unit: z.enum(['Kilograms', 'Grams', 'Milligrams', 'Pounds']),
        value: z.string(),
    }),
    z.object({
        type: z.literal('Volume'),
        unit: z.enum(['Liters', 'Centiliters', 'Milliliters', 'UsFluidOunces', 'UsGallons', 'UsPints']),
        value: z.string(),
    }),
]);

export type DurationV2 = z.infer<typeof DurationV2Runtype>;
export const DurationV2Runtype = z.object({
    unit: z.enum(['Days', 'Weeks']),
    value: z.number(),
});

export type CancellationPolicyV2 = z.infer<typeof CancellationPolicyV2Runtype>;
export const CancellationPolicyV2Runtype = z.object({
    cancellation_window: DurationV2Runtype,
    is_non_cancellable_non_returnable: z.boolean().nullable(),
});

export type OfferAvailabilityV2 = z.infer<typeof OfferAvailabilityV2Runtype>;
export const OfferAvailabilityV2Runtype = z.object({
    factory_lead_time: DurationV2Runtype.nullable(),
    factory_quantity: z.number().nullable(),
    stock: QuantityV2Runtype.nullable(),
});

export type CustomPartPriceBreakV2 = z.infer<typeof CustomPartPriceBreakV2Runtype>;
export const CustomPartPriceBreakV2Runtype = z.object({
    minimum_order_quantity: z.number(),
    minimum_packaging_quantity: z.number(),
    unit_price: MonetaryValueBackendRuntype,
    lead_time: DurationV2Runtype.nullable(),
    description: z.string().nullable(),
});

export type LinkedLocationV2 = z.infer<typeof LinkedLocationV2Runtype>;
export const LinkedLocationV2Runtype = z.discriminatedUnion('type', [
    z.object({
        type: z.literal('Supplier'),
        id: z.string(),
        name: z.string(),
        number: z.string().nullable(),
        stock_location: z.object({ id: z.string(), name: z.string() }),
    }),
    z.object({
        type: z.literal('InventorySite'),
        id: z.string(),
        name: z.string(),
        number: z.string().nullable(),
    }),
]);

// An `InternalPartNumber` linked part is either a component (off-the-shelf or custom, told apart by the
// `id` prefix) or a raw IPN that isn't linked to a component. The backend doesn't tag the two, so these
// are plain union members; the component comes first because it has more fields.
const InternalPartNumberLinkedPartV2Runtypes = [
    z.object({
        type: z.literal('InternalPartNumber'),
        id: z.string(),
        ipn: z.string(),
        revision: z.string().nullable(),
    }),
    z.object({
        type: z.literal('InternalPartNumber'),
        ipn: z.string(),
    }),
] as const;

export type StandardPartOfferLinkedPartV2 = z.infer<typeof StandardPartOfferLinkedPartV2Runtype>;
export const StandardPartOfferLinkedPartV2Runtype = z.union([
    z.object({
        type: z.literal('OffTheShelf'),
        id: z.string(),
        mpn: z.string(),
        manufacturer: ManufacturerLiteDTORuntype,
    }),
    ...InternalPartNumberLinkedPartV2Runtypes,
]);

export type CustomPartOfferLinkedPartV2 = z.infer<typeof CustomPartOfferLinkedPartV2Runtype>;
export const CustomPartOfferLinkedPartV2Runtype = z.union([
    z.object({
        type: z.literal('CustomPart'),
        id: z.string(),
        description: z.string().nullable(),
        custom_part_type: CustomPartTypeRuntype,
    }),
    ...InternalPartNumberLinkedPartV2Runtypes,
]);

export type StandardPartOfferResponseV2 = z.infer<typeof StandardPartOfferResponseV2Runtype>;
export const StandardPartOfferResponseV2Runtype = z.object({
    kind: z.literal(OfferKind.StandardPart),
    id: z.string(),
    linked_part: StandardPartOfferLinkedPartV2Runtype,
    linked_location: LinkedLocationV2Runtype,
    unit_of_measurement: QuantityV2Runtype,
    supplier_part_number: z.string().nullable(),
    offer_number: z.string().nullable(),
    import_context: z.object({ id: z.string(), name: z.string() }).nullable(),
    price_type: PriceTypeRuntype.nullable(),
    price_breaks: z.array(StandardPartPriceBreakRuntype),
    one_time_costs: z.array(OneTimeCostDTORuntype),
    packaging: PackagingRuntype.nullable(),
    availability: OfferAvailabilityV2Runtype.nullable(),
    /** Date only, e.g. `2026-01-15`. */
    valid_from: z.string().nullable(),
    /** Date only, e.g. `2026-06-15`. */
    valid_until: z.string().nullable(),
    cancellation_policy: CancellationPolicyV2Runtype.nullable(),
    notes: z.string().nullable(),
    /** ISO 8601 date-time in UTC. */
    creation_date: z.string(),
    /** The newer equivalent offer, if this one is outdated. */
    superseded_by: z.object({ offer_id: z.string() }).nullable(),
});

export type CustomPartOfferResponseV2 = z.infer<typeof CustomPartOfferResponseV2Runtype>;
export const CustomPartOfferResponseV2Runtype = z.object({
    kind: z.literal(OfferKind.CustomPart),
    id: z.string(),
    linked_part: CustomPartOfferLinkedPartV2Runtype,
    linked_location: LinkedLocationV2Runtype,
    unit_of_measurement: QuantityV2Runtype,
    supplier_part_number: z.string().nullable(),
    offer_number: z.string().nullable(),
    price_type: PriceTypeRuntype.nullable(),
    price_breaks: z.array(CustomPartPriceBreakV2Runtype),
    one_time_costs: z.array(OneTimeCostDTORuntype),
    /** Date only, e.g. `2026-01-15`. */
    valid_from: z.string().nullable(),
    /** Date only, e.g. `2026-04-15`. */
    valid_until: z.string().nullable(),
    cancellation_policy: CancellationPolicyV2Runtype.nullable(),
    notes: z.string().nullable(),
    /** ISO 8601 date-time in UTC. */
    creation_date: z.string(),
});

export type OfferResponseV2 = z.infer<typeof OfferResponseV2Runtype>;
export const OfferResponseV2Runtype = z.discriminatedUnion('kind', [
    StandardPartOfferResponseV2Runtype,
    CustomPartOfferResponseV2Runtype,
]);

// Each filter is optional, and an offer has to pass every filter that is given. The backend rejects a
// request without any filter.
export type FindOffersRequest = z.infer<typeof FindOffersRequestRuntype>;
export const FindOffersRequestRuntype = z.object({
    filters: z.object({
        part_ids: z.array(z.string()).min(1).max(100).nullish(),
        import_context_ids: z.array(z.string()).min(1).max(100).nullish(),
    }),
    /** Omit on the first page; afterwards pass the previous result's `pagination.next_cursor`. */
    cursor: z.string().nullish(),
    /** Defaults to 100. */
    limit: z.number().int().min(1).max(1000).nullish(),
});

export type FindOffersResult = z.infer<typeof FindOffersResultRuntype>;
export const FindOffersResultRuntype = z.object({
    offers: z.array(OfferResponseV2Runtype),
    pagination: z.object({
        next_cursor: z.string().nullable(),
        has_more: z.boolean(),
    }),
});
